/**
 * NOVA watching itself.
 *
 * These are the resources this dashboard actually runs on, in its own resource
 * group. It is the one place on the board where per-resource Azure Monitor
 * metrics are read: metrics are a per-resource API, so pulling them for all 94
 * workloads would be hundreds of calls a cycle, while pulling them for NOVA's
 * own handful is cheap and immediately useful.
 */

import { Badge, Card, MiniSparkline, Typography } from "@norican/react";

import type { NovaFootprint, NovaResource } from "@/lib/api";

import { PanelCard } from "./panel-card";
import { formatMetric, STATE_LABEL, STATE_TONE } from "./status";

interface NovaPanelProps {
  footprint: NovaFootprint;
}

export function NovaPanel({ footprint }: NovaPanelProps) {
  const withMetrics = footprint.resources.filter((resource) => resource.metrics.length > 0);
  const rest = footprint.resources.filter((resource) => resource.metrics.length === 0);

  return (
    <PanelCard
      title="NOVA's own footprint"
      caption={`${footprint.resource_group} · ${footprint.subscription_name} · ${footprint.resources.length} resources`}
    >
      {!footprint.reachable ? (
        <Typography variant="body-sm" tone="muted">
          {footprint.detail}
        </Typography>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 xl:grid-cols-3">
            {withMetrics.map((resource) => (
              <NovaResourceCard key={resource.id} resource={resource} />
            ))}
          </div>

          {rest.length > 0 && (
            <div className="flex flex-col gap-2">
              <Typography variant="label" as="h3">
                Supporting resources
              </Typography>
              <div className="flex flex-wrap gap-1.5">
                {rest.map((resource) => (
                  <Badge key={resource.id} tone="neutral" variant="outline" size="sm">
                    {resource.name} · {resource.kind}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </PanelCard>
  );
}

function NovaResourceCard({ resource }: { resource: NovaResource }) {
  return (
    <Card variant="subtle">
      <Card.Body padding="sm">
        <div className="flex flex-col gap-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex flex-col">
              <Typography variant="body-sm" as="h3">
                <span className="text-fg font-medium">{resource.name}</span>
              </Typography>
              <Typography variant="micro" tone="muted">
                {resource.kind} · {resource.location}
              </Typography>
            </div>
            <Badge tone={STATE_TONE[resource.state]} variant="subtle" size="sm">
              {resource.status || STATE_LABEL[resource.state]}
            </Badge>
          </div>

          <div className="flex flex-col gap-2">
            {resource.metrics.map((metric) => (
              <div key={metric.name} className="flex items-center justify-between gap-3">
                <span className="text-fg-muted text-xs">{metric.label}</span>
                {metric.error ? (
                  <span className="text-fg-subtle text-xs">unavailable</span>
                ) : (
                  <span className="flex items-center gap-2">
                    {metric.series.length > 1 && (
                      <MiniSparkline
                        data={metric.series}
                        tone="accent"
                        endMarker="dot"
                        width={64}
                        height={16}
                        aria-label={`${metric.label} trend for ${resource.name}`}
                      />
                    )}
                    <span className="text-fg text-xs tabular-nums">
                      {formatMetric(metric.name, metric.latest)}
                    </span>
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
